// admin-ui.ts — the pieces the two admin screens share.
//
// User Management (#/users) and Audit Trail (#/audit) are separate pages with
// their own nav entries, but they are the same KIND of page: an admin-gated
// AG Grid with a filter bar and a modal. The console client is otherwise
// deliberately non-DRY (each screen carries its own jsonFetch), and that was
// fine while there was one admin screen; two screens sharing a chrome, a
// grid theme, an avatar renderer and a modal is where copy-paste starts
// producing two subtly different admin UIs.
//
// Colors come from --hc-* tokens only (styles.css's hard rule: no rule may
// name a bare hex), and AG Grid takes its palette from the per-user
// appearance seat through gridThemeParams() — never a hardcoded palette.

import {
  AllCommunityModule,
  ModuleRegistry,
  createGrid,
  themeQuartz,
  type GridApi,
  type GridOptions,
} from 'ag-grid-community';
import { attachGridScrollRails } from 'pi-stack/client/scroll-rails.js';
import { gridThemeParams } from './appearance.js';

// A module that CREATES a grid registers AG's modules itself — the rule both
// Bridge grid modules already follow (glance-grid.ts, ops-procs-grid.ts each
// carry this line; read glance-grid.ts's note for why it is the whole
// community set and not a hand-picked list). This file was the one grid
// factory that did not, and worked only because app.ts statically imports
// home.js, whose top-level `registerModules` happens to run first: makeGrid's
// correctness was a property of an UNRELATED module's import order.
//
// The failure mode is not a blank grid. AG 36 makes `api.getColumnState` a
// module api ("Unable to use `api.getColumnState` as `ColumnApiModule` is not
// registered"), and the scroll rails read it on every re-measure — so an
// admin grid reached without home.js in the graph froze its horizontal rail
// in whatever state it started in. Measured 2026-09-09 in headless Chromium
// on makeGrid with home.ts out of the bundle: 29 throws, the rail off through
// 40px of real overflow and still off after a hide, 6 of 7 rail arms red —
// the owner's reported symptom exactly, from a second direction. Registration
// is idempotent, so declaring it here costs one call and removes the ordering
// dependency. `grid-rails-fit.test.mjs`'s M arm is the control: it builds a
// grid through makeGrid ALONE and refuses if the column-state api is missing.
ModuleRegistry.registerModules([AllCommunityModule]);
/** Mirrors the server's roles.ts vocabulary. The server is the authority
 * on what a role MAY DO; the client only ever labels it. */
export type Role = 'admin' | 'release_owner' | 'member' | 'viewer';
export const ROLES: Role[] = ['admin', 'release_owner', 'member', 'viewer'];

export async function jsonFetch<T>(
  method: string,
  url: string,
  body?: unknown,
): Promise<{ status: number; data: T | null }> {
  try {
    const res = await fetch(url, {
      method,
      headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    let data: T | null = null;
    try { data = (await res.json()) as T; } catch { /* tolerate non-JSON */ }
    return { status: res.status, data };
  } catch {
    return { status: 0, data: null };
  }
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

// adminChrome is GONE (shared-shell): the admin pages' header was the second
// of two near-twin shells — same class name as mountHome's, no appearance
// control, no account menu. All three pages mount shell.ts's ONE shell now
// (R63); the page title and row count that lived up here render in the
// page's own .hc-pagebar row below it (R68).

/** Admin-only screens must SAY so rather than rendering an empty grid — an
 * empty table and a refusal look identical, and one of them is a bug. */
export function renderRefusal(root: HTMLElement, status: number): void {
  const main = root.querySelector('.hc-main') as HTMLElement;
  main.innerHTML = '';
  const box = el('div', 'hc-placeholder');
  box.appendChild(el('h2', '', status === 401 ? 'Not signed in' : 'Admin only'));
  box.appendChild(el('p', 'hc-status', status === 401
    ? 'Your session has expired. Sign in again to continue.'
    : 'This page is available to administrators. Ask an admin if you need access.'));
  main.appendChild(box);
}

/** R59c (users-audit-parity, ruling default taken): the rowHeight:44 /
 * headerHeight:38 GridOptions this used to force are DELETED, not retuned —
 * the admin grids now take their row metrics from the SAME theme derivation
 * the release grid rides (gridThemeParams sets fonts/padding; AG's quartz
 * theme derives the heights), so the three grids' headers and cells match
 * by construction instead of by two numbers kept in sync by hand.
 * animateRows ON for the same reason home.ts turned it on: these pages
 * animate like the rest of the Bridge now, and rows that jump between sorts
 * would be the one obvious inconsistency. AG owns that transition and the
 * browser suppresses it for reduced-motion users (home.ts's note). */
export function makeGrid<T>(host: HTMLElement, options: GridOptions<T>): GridApi<T> {
  const api = createGrid<T>(host, {
    theme: themeQuartz.withParams(gridThemeParams()),
    animateRows: true,
    defaultColDef: { sortable: true, resizable: true },
    ...options,
  });
  // Owner item 67: the transcript's rail treatment on every AG grid (the
  // shared pi-stack layer — always-visible rails, pinned-aware horizontal
  // span, no overscroll bounce). Colors ride --sb-rail-* mapped in
  // styles.css. Self-detaches on gridPreDestroyed.
  attachGridScrollRails(host, api);
  return api;
}

// The modal helper that lived here (Escape + backdrop + focus return, but
// no trap, no exit animation, and a document keydown listener it never
// removed: one leaked per page mount) is DELETED — its name is deliberately
// not written here, because dialog.test.mjs proves the deletion by absence
// and a comment naming it would false-positive that grep (the CLAUDE.md
// dist-comment trap). Dialogs open through the one engine in dialog.ts
// (seam dialog-system; the deletion is what closed the ESC-handler leak).

// ---------------------------------------------------------------------
// Cell renderers shared by both grids
// ---------------------------------------------------------------------

/** Google avatar, with initials as the fallback. Always a real DOM node —
 * AG Grid cell renderers returning HTML strings cannot be styled per-theme
 * and cannot carry an error handler. A broken image URL falls back to
 * initials rather than rendering a browser's broken-image glyph. */
export function avatarNode(pictureUrl: string | null, name: string | null, email: string): HTMLElement {
  const wrap = el('span', 'hc-avatar');
  const initials = (name?.trim() || email)
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
  const fallback = (): void => {
    wrap.textContent = initials;
    wrap.classList.add('hc-avatar-initials');
  };
  if (pictureUrl) {
    const img = document.createElement('img');
    img.src = pictureUrl;
    img.alt = '';
    img.referrerPolicy = 'no-referrer';
    img.addEventListener('error', fallback);
    wrap.appendChild(img);
  } else {
    fallback();
  }
  return wrap;
}

export function rolePill(role: Role | string): HTMLElement {
  const e = el('span', `hc-pill hc-pill-role-${String(role).replace(/_/g, '-')}`);
  e.textContent = ROLE_LABELS[role as Role] ?? String(role);
  return e;
}

/** The same pill as an HTML string — the role dropdown's CLOSED face
 * (dropdown.ts labelHtml). Twin of rolePill above, kept adjacent so the
 * class template and label lookup cannot drift apart; labels/classes are
 * client constants, so the string is trusted by construction. */
export function rolePillHtml(role: Role | string): string {
  const cls = `hc-pill hc-pill-role-${String(role).replace(/_/g, '-')}`;
  return `<span class="${cls}">${ROLE_LABELS[role as Role] ?? String(role)}</span>`;
}

/** Role vocabulary, mirrored client-side for labels only. The SERVER decides
 * what a role may do; this is presentation. */
export const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  release_owner: 'Release owner',
  member: 'Member',
  viewer: 'Viewer',
};

export const ROLE_BLURBS: Record<string, string> = {
  admin: 'Full access, including user management and the audit trail.',
  release_owner: 'Can create releases, and interact with every release.',
  member: 'Can access releases and take part in chat sessions.',
  viewer: 'Read-only: can open releases, but cannot send chat messages.',
};

/** "3 minutes ago" / "—". Absolute time goes in the title attribute, because
 * a relative time you cannot resolve is useless in an audit conversation. */
export function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const secs = Math.round((Date.now() - t) / 1000);
  if (secs < 0) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function absoluteTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '';
}

/** A cell that reads as a time and resolves on hover. */
export function timeCell(iso: string | null): HTMLElement {
  const e = el('span', 'hc-time', relativeTime(iso));
  if (iso) e.dataset.tip = absoluteTime(iso);
  return e;
}
